import React, { useCallback, useEffect, useState } from 'react';
import {
  ArrowTopRightOnSquareIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  PhotoIcon,
  PlayCircleIcon,
} from '@heroicons/react/24/outline';
import type { AlbumMediaItem, AlbumType, MediaAlbum } from '../../types/buildExpansion';
import apiService from '../../services/ApiService';
import Modal from '../ui/Modal';
import Button from '../ui/Button';
import { resolvePublicFileUrl } from '../../utils/imageUrls';

/**
 * Album grid plus an accessible viewer. Media loads lazily when an album is
 * opened. External videos embed via iframe only for YouTube and Vimeo, with
 * the embed URL rebuilt from the parsed watch URL; any other provider falls
 * back to an outbound link. Arrow keys move between items, Escape closes.
 */

const ALBUM_TYPE_LABELS: Record<AlbumType, string> = {
  overview: 'Overview',
  exterior: 'Exterior',
  interior: 'Interior',
  engine_bay: 'Engine bay',
  suspension: 'Suspension',
  fabrication: 'Fabrication',
  installation: 'Installation',
  before_after: 'Before and after',
  event: 'Event',
  track: 'Track',
  dyno: 'Dyno',
  professional_shoot: 'Professional shoot',
  restoration: 'Restoration',
  other: 'Other',
};

/**
 * Builds a safe embed URL. Only YouTube and Vimeo are ever embedded, and the
 * URL is reconstructed from the parsed video id; the stored video_url is
 * never injected into an iframe src directly.
 */
export const getVideoEmbedUrl = (
  provider: string | null,
  videoUrl: string | null
): string | null => {
  if (!provider || !videoUrl) return null;
  let parsed: URL;
  try {
    parsed = new URL(videoUrl);
  } catch {
    return null;
  }
  if (parsed.protocol !== 'https:' && parsed.protocol !== 'http:') return null;
  const host = parsed.hostname.replace(/^www\./i, '').toLowerCase();
  const providerKey = provider.toLowerCase();

  if (providerKey === 'youtube') {
    let id: string | null = null;
    if (host === 'youtu.be') {
      id = parsed.pathname.slice(1).split('/')[0] || null;
    } else if (host === 'youtube.com' || host === 'm.youtube.com' || host === 'youtube-nocookie.com') {
      if (parsed.pathname === '/watch') {
        id = parsed.searchParams.get('v');
      } else {
        const match = parsed.pathname.match(/^\/(embed|shorts|live|v)\/([^/?#]+)/);
        if (match) id = match[2];
      }
    }
    if (id && /^[A-Za-z0-9_-]{5,20}$/.test(id)) {
      return `https://www.youtube.com/embed/${encodeURIComponent(id)}`;
    }
    return null;
  }

  if (providerKey === 'vimeo') {
    if (host === 'vimeo.com' || host === 'player.vimeo.com') {
      const match = parsed.pathname.match(/(\d{6,12})/);
      if (match) return `https://player.vimeo.com/video/${match[1]}`;
    }
    return null;
  }

  return null;
};

const isSafeHttpUrl = (url: string | null): url is string => {
  if (!url) return false;
  try {
    const parsed = new URL(url);
    return parsed.protocol === 'https:' || parsed.protocol === 'http:';
  } catch {
    return false;
  }
};

interface AlbumViewerProps {
  album: MediaAlbum;
  vehicleId: number;
  onClose: () => void;
}

const AlbumViewer: React.FC<AlbumViewerProps> = ({ album, vehicleId, onClose }) => {
  const [items, setItems] = useState<AlbumMediaItem[] | null>(null);
  const [loadError, setLoadError] = useState(false);
  const [index, setIndex] = useState(0);

  const loadMedia = useCallback(async () => {
    setLoadError(false);
    setItems(null);
    try {
      const media = await apiService.getAlbumMedia(vehicleId, album.id);
      setItems(media);
      setIndex(0);
    } catch {
      setLoadError(true);
    }
  }, [vehicleId, album.id]);

  useEffect(() => {
    loadMedia();
  }, [loadMedia]);

  const count = items?.length ?? 0;
  const goPrev = useCallback(() => {
    if (count > 1) setIndex((i) => (i - 1 + count) % count);
  }, [count]);
  const goNext = useCallback(() => {
    if (count > 1) setIndex((i) => (i + 1) % count);
  }, [count]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        goPrev();
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        goNext();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [goPrev, goNext]);

  const current = items && items.length > 0 ? items[Math.min(index, items.length - 1)] : null;
  const typeLabel = ALBUM_TYPE_LABELS[album.album_type] || 'Album';
  const embedUrl =
    current?.media_kind === 'video_external'
      ? getVideoEmbedUrl(current.video_provider, current.video_url)
      : null;

  return (
    <Modal
      isOpen
      onClose={onClose}
      title={album.title}
      description={`${typeLabel} · ${album.item_count} ${album.item_count === 1 ? 'item' : 'items'}`}
      size="xl"
    >
      {items === null && !loadError && (
        <div className="py-16 text-center" role="status" aria-busy="true">
          <span className="sr-only">Loading album media…</span>
          <div className="mx-auto h-48 max-w-md rounded-lg bg-[#111] animate-pulse motion-reduce:animate-none" aria-hidden />
        </div>
      )}

      {loadError && (
        <div className="py-12 text-center">
          <p className="text-sm text-gray-300">This album did not load.</p>
          <div className="mt-4 flex justify-center">
            <Button variant="secondary" size="sm" onClick={loadMedia}>
              Try again
            </Button>
          </div>
        </div>
      )}

      {items !== null && items.length === 0 && !loadError && (
        <p className="py-12 text-center text-sm text-gray-400">Nothing in this album yet.</p>
      )}

      {current && (
        <div>
          <div className="bg-[#0a0a0a]/70 rounded-lg overflow-hidden flex items-center justify-center min-h-[240px]">
            {current.media_kind === 'image' ? (
              <img
                src={resolvePublicFileUrl(current.image_url) || current.image_url}
                alt={current.alt_text || current.title || `${album.title}, item ${index + 1}`}
                className="max-h-[60vh] w-auto max-w-full object-contain"
              />
            ) : embedUrl ? (
              <div className="w-full aspect-video">
                <iframe
                  src={embedUrl}
                  title={current.title || `${album.title} video ${index + 1}`}
                  className="w-full h-full"
                  allow="accelerometer; encrypted-media; picture-in-picture"
                  allowFullScreen
                />
              </div>
            ) : (
              <div className="py-16 px-4 text-center">
                <PlayCircleIcon className="w-10 h-10 text-gray-500 mx-auto" aria-hidden />
                <p className="text-sm text-gray-300 mt-3">
                  {current.title || 'External video'}
                </p>
                {isSafeHttpUrl(current.video_url) ? (
                  <a
                    href={current.video_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 text-sm font-medium text-orange-300 hover:text-orange-200 transition-colors duration-200 mt-2 focus-visible:outline focus-visible:outline-2 focus-visible:outline-orange-400 rounded"
                  >
                    Watch video
                    <ArrowTopRightOnSquareIcon className="w-4 h-4" aria-hidden />
                    <span className="sr-only">(opens in a new tab)</span>
                  </a>
                ) : (
                  <p className="text-xs text-gray-500 mt-2">This video link is unavailable.</p>
                )}
              </div>
            )}
          </div>

          {(current.title || current.alt_text) && (
            <p className="text-sm text-gray-300 mt-3">{current.title || current.alt_text}</p>
          )}
          {album.contributor_credit && (
            <p className="text-xs text-gray-500 mt-1">Credit: {album.contributor_credit}</p>
          )}

          <div className="flex items-center justify-between gap-3 mt-4">
            <button
              type="button"
              onClick={goPrev}
              disabled={count <= 1}
              aria-label="Previous item"
              className="btn-icon cursor-pointer disabled:opacity-40 disabled:cursor-default"
            >
              <ChevronLeftIcon className="w-5 h-5" aria-hidden />
            </button>
            <p className="text-sm text-gray-400 tabular-nums" aria-live="polite">
              {index + 1} of {count}
            </p>
            <button
              type="button"
              onClick={goNext}
              disabled={count <= 1}
              aria-label="Next item"
              className="btn-icon cursor-pointer disabled:opacity-40 disabled:cursor-default"
            >
              <ChevronRightIcon className="w-5 h-5" aria-hidden />
            </button>
          </div>
        </div>
      )}
    </Modal>
  );
};

export interface MediaAlbumsProps {
  albums: MediaAlbum[];
  vehicleId: number;
  /** Optional hook when an album opens (analytics, routing). Viewer still opens. */
  onOpenAlbum?: (album: MediaAlbum) => void;
  className?: string;
}

export const MediaAlbums: React.FC<MediaAlbumsProps> = ({
  albums,
  vehicleId,
  onOpenAlbum,
  className = '',
}) => {
  const [openAlbum, setOpenAlbum] = useState<MediaAlbum | null>(null);

  if (albums.length === 0) return null;

  const handleOpen = (album: MediaAlbum) => {
    setOpenAlbum(album);
    onOpenAlbum?.(album);
  };

  return (
    <section className={`card p-5 sm:p-7 ${className}`} aria-label="Media albums">
      <div className="flex items-baseline gap-3 border-b border-white/[0.06] pb-3">
        <h3 className="font-display text-base sm:text-lg font-semibold tracking-tight text-white">Albums</h3>
        <span className="font-mono text-[11px] text-gray-600 tabular-nums" aria-hidden>
          {String(albums.length).padStart(2, '0')}
        </span>
      </div>
      <ul className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4 mt-4 list-none">
        {albums.map((album) => {
          const coverUrl = resolvePublicFileUrl(album.cover_url);
          const typeLabel = ALBUM_TYPE_LABELS[album.album_type] || 'Album';
          const countLabel = `${album.item_count} ${album.item_count === 1 ? 'item' : 'items'}`;
          return (
            <li key={album.id}>
              <button
                type="button"
                onClick={() => handleOpen(album)}
                aria-label={`Open album ${album.title}, ${typeLabel}, ${countLabel}`}
                className="group w-full text-left cursor-pointer rounded-xl overflow-hidden focus-visible:outline focus-visible:outline-2 focus-visible:outline-orange-400"
              >
                <div className="aspect-[4/3] rounded-xl bg-[#0a0a0a]/70 flex items-center justify-center overflow-hidden ring-1 ring-white/5 group-hover:ring-white/20 transition-shadow duration-200">
                  {coverUrl ? (
                    <img
                      src={coverUrl}
                      alt=""
                      className="w-full h-full object-cover motion-safe:transition-transform motion-safe:duration-500 motion-safe:group-hover:scale-[1.03]"
                    />
                  ) : (
                    <PhotoIcon className="w-8 h-8 text-gray-600" aria-hidden />
                  )}
                </div>
                <div className="pt-2.5 pb-1">
                  <p className="text-sm font-medium text-white truncate group-hover:text-orange-100 transition-colors duration-200">
                    {album.title}
                  </p>
                  <p className="font-mono text-[10px] uppercase tracking-[0.12em] text-gray-500 mt-1">
                    {typeLabel} · {countLabel}
                  </p>
                </div>
              </button>
            </li>
          );
        })}
      </ul>

      {openAlbum && (
        <AlbumViewer album={openAlbum} vehicleId={vehicleId} onClose={() => setOpenAlbum(null)} />
      )}
    </section>
  );
};

export default MediaAlbums;
